/**
 * The hourly breakdown under a bucket row: fetched the first time a bucket is opened, once per run per tab.
 * Uses the filters the report was last run with, so the hours add up to the numbers on screen.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { postHourly, type HourlyKind, type WithHour } from './api';
import type { HourlyByBucket } from './adapt';
import { useReport } from './ReportContext';

export interface HourlyState<T> {
  /** Hourly rows are only for live data; the sample set has none. */
  available: boolean;
  data: HourlyByBucket<T> | null;
  loading: boolean;
  error: string | null;
  /** Start the fetch if it has not run for this run of the report. */
  load: () => void;
}

export function useHourly<A, T>(kind: HourlyKind, adapt: (rows: WithHour<A>[]) => HourlyByBucket<T>): HourlyState<T> {
  const { mode, ranFilters, runToken, resolveAgent } = useReport();
  const [state, setState] = useState<{ token: number; data: HourlyByBucket<T> | null; loading: boolean; error: string | null }>(
    { token: runToken, data: null, loading: false, error: null });
  const started = useRef(-1);

  // A new run of the report makes whatever was fetched before stale.
  useEffect(() => { setState({ token: runToken, data: null, loading: false, error: null }); started.current = -1; }, [runToken]);

  const load = useCallback(() => {
    if (mode !== 'live' || started.current === runToken) return;
    started.current = runToken;
    const token = runToken;
    setState(s => ({ ...s, loading: true, error: null }));
    postHourly<A>(ranFilters, resolveAgent(ranFilters.agent).agentId, kind)
      .then(res => {
        if (started.current !== token) return;
        if (!res?.success) throw new Error('The hourly breakdown could not be loaded');
        setState({ token, data: adapt(res.rows), loading: false, error: null });
      })
      .catch(e => {
        if (started.current !== token) return;
        started.current = -1;                      // let the next click try again
        setState(s => ({ ...s, loading: false, error: e instanceof Error ? e.message : 'The hourly breakdown could not be loaded' }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, runToken, ranFilters, kind]);

  return { available: mode === 'live', data: state.token === runToken ? state.data : null, loading: state.loading, error: state.error, load };
}

/** 9 -> "09:00–10:00" */
export const hourRange = (h: number) => `${String(h).padStart(2, '0')}:00–${String((h + 1) % 24).padStart(2, '0')}:00`;
